// /src/components/admin/SocialImagesTab.jsx
// Social Media Image Slots - Manage 8 default images for social media previews

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabase, supabaseFetch } from '../../lib/supabase';
import { RefreshCw, Upload, X } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import ConfirmationModal from '../core/ConfirmationModal';

const SocialImagesTab = ({ currentUser }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [uploading, setUploading] = useState({});
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showInfo = (message) => setInfoModal({ visible: true, message });

  const loadSlots = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('admin_social_images', {
        select: '*',
        order: 'id.asc'
      });

      if (error) {
        console.error('Error loading social image slots:', error);
        return;
      }

      setSlots(data || []);
    } catch (err) {
      console.error('Error loading social image slots:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlots();
  }, []);

  const handleFileSelect = async (slotId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showInfo(t('selectImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showInfo(t('imageSizeLimit'));
      return;
    }

    setUploading({ ...uploading, [slotId]: true });

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `social-${slotId}-${Date.now()}.${fileExt}`;
      const filePath = `social-images/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        showInfo(t('failedToUploadImage'));
        setUploading({ ...uploading, [slotId]: false });
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;

      // Update database with new image URL
      const { error: updateError } = await supabaseFetch.update('admin_social_images', slotId, {
        image_url: publicUrl,
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.id
      });

      if (updateError) {
        console.error('Error updating image slot:', updateError);
        showInfo(t('failedToUpdateSlot'));
        setUploading({ ...uploading, [slotId]: false });
        return;
      }

      loadSlots();
    } catch (err) {
      console.error('Error handling file upload:', err);
      showInfo(t('failedToUploadImage'));
    } finally {
      setUploading({ ...uploading, [slotId]: false });
    }
  };

  const handleClearImage = async (slotId) => {
    if (!window.confirm(t('removeImageConfirm'))) return;

    try {
      const { error } = await supabaseFetch.update('admin_social_images', slotId, {
        image_url: null,
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.id
      });

      if (error) {
        console.error('Error clearing image:', error);
        showInfo(t('failedToClearImage'));
        return;
      }

      loadSlots();
    } catch (err) {
      console.error('Error clearing image:', err);
      showInfo(t('failedToClearImage'));
    }
  };

  const handleUpdateLabel = async (slotId, currentLabel) => {
    const newLabel = window.prompt(t('enterNewLabel'), currentLabel);
    if (!newLabel || newLabel === currentLabel) return;

    try {
      const { error } = await supabaseFetch.update('admin_social_images', slotId, {
        label: newLabel,
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.id
      });

      if (error) {
        console.error('Error updating label:', error);
        showInfo(t('failedToUpdateLabel'));
        return;
      }

      loadSlots();
    } catch (err) {
      console.error('Error updating label:', err);
      showInfo(t('failedToUpdateLabel'));
    }
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1A1A1A'
    },
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '500'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '20px'
    },
    slot: {
      backgroundColor: theme?.bg || '#F5F5F5',
      borderRadius: '8px',
      padding: '16px',
      border: `1px solid ${theme?.border || '#DDD'}`
    },
    slotHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    slotNumber: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: theme?.primary || '#024B99'
    },
    slotLabel: {
      fontSize: '12px',
      color: theme?.text?.secondary || '#666',
      cursor: 'pointer',
      textDecoration: 'underline'
    },
    imagePreview: {
      width: '100%',
      height: '160px',
      backgroundColor: '#F0F0F0',
      borderRadius: '6px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    placeholder: {
      color: theme?.text?.secondary || '#666',
      fontSize: '13px',
      textAlign: 'center'
    },
    buttons: {
      display: 'flex',
      gap: '8px'
    },
    uploadButton: {
      flex: 1,
      padding: '8px 12px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.primary || '#024B99',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      fontSize: '12px',
      fontWeight: '500',
      position: 'relative'
    },
    clearButton: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px'
    },
    fileInput: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      cursor: 'pointer'
    },
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666'
    },
    updatedInfo: {
      fontSize: '11px',
      color: theme?.text?.secondary || '#666',
      marginTop: '8px'
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{t('socialMediaPreviewImages')}</div>
          <div style={{ fontSize: '13px', color: theme?.text?.secondary || '#666', marginTop: '4px' }}>
            {t('socialImagesDescription')}
          </div>
        </div>
        <button
          onClick={loadSlots}
          disabled={loading}
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'default' : 'pointer'
          }}
        >
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
      </div>

      {loading ? (
        <div style={styles.loadingBox}>{t('loadingImageSlots')}</div>
      ) : (
        <div style={styles.grid}>
          {slots.map(slot => (
            <div key={slot.id} style={styles.slot}>
              <div style={styles.slotHeader}>
                <span style={styles.slotNumber}>{t('slot')} {slot.id}</span>
                <span
                  style={styles.slotLabel}
                  onClick={() => handleUpdateLabel(slot.id, slot.label)}
                >
                  {slot.label}
                </span>
              </div>

              <div style={styles.imagePreview}>
                {slot.image_url ? (
                  <img src={slot.image_url} alt={slot.label} loading="lazy" style={styles.image} />
                ) : (
                  <div style={styles.placeholder}>{t('noImageUploaded')}</div>
                )}
              </div>

              <div style={styles.buttons}>
                <label style={styles.uploadButton}>
                  {uploading[slot.id] ? (
                    t('uploading')
                  ) : (
                    <>
                      <Upload size={14} />
                      {t('upload')}
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect(slot.id, e)}
                    disabled={uploading[slot.id]}
                    style={styles.fileInput}
                  />
                </label>

                {slot.image_url && (
                  <button
                    onClick={() => handleClearImage(slot.id)}
                    style={styles.clearButton}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {slot.updated_at && (
                <div style={styles.updatedInfo}>
                  {t('lastUpdated')}: {new Date(slot.updated_at).toLocaleDateString(formattingLocale)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
        okLabel="OK"
      />
    </div>
  );
};

export default SocialImagesTab;
